import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Space, Table, Typography } from "antd";
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";

const { Text } = Typography;
const PREFIJO_TELEFONO = "549";

const normalizarCuenta = (cuenta, mensajeId, index) => ({
  key: cuenta._id || `${mensajeId}-${cuenta.cuenta}-${index}`,
  id: cuenta._id || cuenta.cuenta,
  cuenta: String(cuenta.cuenta ?? "").trim(),
  nombre: cuenta.nombre || "",
});

const normalizarMensaje = (mensaje) => ({
  key: mensaje._id,
  id: mensaje._id,
  numero: String(mensaje.numero || "").trim(),
  cuentas: Array.isArray(mensaje.datosCuenta)
    ? mensaje.datosCuenta.map((cuenta, index) => normalizarCuenta(cuenta, mensaje._id, index))
    : [],
});

const contarListasActualizadas = (listasActualizadas = {}) =>
  Object.values(listasActualizadas).reduce((total, cantidad) => total + Number(cantidad || 0), 0);

const normalizarTelefonoConPrefijo = (numero) => {
  const digits = String(numero || "").replace(/\D/g, "");
  if (!digits) return PREFIJO_TELEFONO;
  return digits.startsWith(PREFIJO_TELEFONO) ? digits : `${PREFIJO_TELEFONO}${digits}`;
};

const CCuentas = () => {
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [searchNumero, setSearchNumero] = useState("");
  const [searchCuenta, setSearchCuenta] = useState("");
  const [searchNombre, setSearchNombre] = useState("");
  const [modalCuentaOpen, setModalCuentaOpen] = useState(false);
  const [modalTelefonoOpen, setModalTelefonoOpen] = useState(false);
  const [mensajeSeleccionado, setMensajeSeleccionado] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [formCuenta] = Form.useForm();
  const [formTelefono] = Form.useForm();

  const user = JSON.parse(localStorage.getItem("user"));
  const empresaId = user?.user?.idCooperativa;
  const token = user?.token;
  const api = process.env.REACT_APP_API_URL;

  const fetchMensajes = useCallback(async () => {
    if (!empresaId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${api}/mensajes/${empresaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMensajes((response.data || []).map(normalizarMensaje));
    } catch (error) {
      if (error?.response?.status === 404) {
        setMensajes([]);
        return;
      }

      console.error("Error al cargar cuentas:", error);
      message.error("Error al cargar las cuentas.");
    } finally {
      setLoading(false);
    }
  }, [api, empresaId, token]);

  useEffect(() => {
    fetchMensajes();
  }, [fetchMensajes]);

  const mensajesFiltrados = useMemo(() => {
    const numeroFiltro = searchNumero.toLowerCase();
    const cuentaFiltro = searchCuenta.toLowerCase();
    const nombreFiltro = searchNombre.toLowerCase();
    const filtraCuentas = Boolean(cuentaFiltro || nombreFiltro);

    return mensajes
      .map((mensaje) => {
        const numero = String(mensaje.numero || "").toLowerCase();
        const coincideNumero = !numeroFiltro || numero.includes(numeroFiltro);
        const cuentasFiltradas = mensaje.cuentas.filter((cuenta) => {
          const cuentaTexto = String(cuenta.cuenta || "").toLowerCase();
          const nombreTexto = String(cuenta.nombre || "").toLowerCase();

          return (
            (!cuentaFiltro || cuentaTexto.includes(cuentaFiltro)) &&
            (!nombreFiltro || nombreTexto.includes(nombreFiltro))
          );
        });

        return {
          ...mensaje,
          cuentasVisibles: filtraCuentas ? cuentasFiltradas : mensaje.cuentas,
          coincideNumero,
        };
      })
      .filter((mensaje) => mensaje.coincideNumero && (!filtraCuentas || mensaje.cuentasVisibles.length > 0));
  }, [mensajes, searchCuenta, searchNombre, searchNumero]);

  const limpiarFiltros = () => {
    setSearchNumero("");
    setSearchCuenta("");
    setSearchNombre("");
  };

  const abrirModalCuenta = (mensaje) => {
    setMensajeSeleccionado(mensaje);
    formCuenta.resetFields();
    setModalCuentaOpen(true);
  };

  const cerrarModalCuenta = () => {
    setModalCuentaOpen(false);
    setMensajeSeleccionado(null);
    formCuenta.resetFields();
  };

  const abrirModalTelefono = () => {
    formTelefono.setFieldsValue({ numero: PREFIJO_TELEFONO, cuentas: [{ cuenta: "", nombre: "" }] });
    setModalTelefonoOpen(true);
  };

  const cerrarModalTelefono = () => {
    setModalTelefonoOpen(false);
    formTelefono.resetFields();
  };

  const agregarCuenta = async () => {
    if (!mensajeSeleccionado) return;

    try {
      const values = await formCuenta.validateFields();
      setSavingKey(`cuenta-${mensajeSeleccionado.id}`);
      await axios.post(
        `${api}/mensajes/${empresaId}/${mensajeSeleccionado.id}/cuentas`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Cuenta agregada correctamente.");
      cerrarModalCuenta();
      await fetchMensajes();
    } catch (error) {
      if (error?.errorFields) return;

      if (error?.response?.status === 409) {
        message.warning(error.response.data?.message || "La cuenta ya está vinculada a este teléfono.");
        return;
      }

      console.error("Error al agregar cuenta:", error);
      message.error("No se pudo agregar la cuenta.");
    } finally {
      setSavingKey(null);
    }
  };

  const agregarTelefono = async () => {
    try {
      const values = await formTelefono.validateFields();
      const payload = {
        ...values,
        numero: normalizarTelefonoConPrefijo(values.numero),
      };
      setSavingKey("telefono");
      await axios.post(`${api}/mensajes/${empresaId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Teléfono agregado correctamente.");
      cerrarModalTelefono();
      await fetchMensajes();
    } catch (error) {
      if (error?.errorFields) return;

      if (error?.response?.status === 409) {
        message.warning(error.response.data?.message || "No se pudo agregar porque ya existe.");
        return;
      }

      console.error("Error al agregar teléfono:", error);
      message.error("No se pudo agregar el teléfono.");
    } finally {
      setSavingKey(null);
    }
  };

  const eliminarCuenta = async (mensajeId, cuentaId) => {
    setDeletingKey(`${mensajeId}-${cuentaId}`);
    try {
      const response = await axios.delete(`${api}/mensajes/${empresaId}/${mensajeId}/cuentas/${cuentaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const removidos = contarListasActualizadas(response.data?.listasActualizadas);
      message.success(
        removidos > 0
          ? `Cuenta eliminada correctamente. También se quitó de ${removidos} lista(s) de difusión.`
          : "Cuenta eliminada correctamente."
      );
      await fetchMensajes();
    } catch (error) {
      console.error("Error al eliminar cuenta:", error);
      message.error("No se pudo eliminar la cuenta.");
    } finally {
      setDeletingKey(null);
    }
  };

  const eliminarTelefono = async (mensajeId) => {
    setDeletingKey(mensajeId);
    try {
      const response = await axios.delete(`${api}/mensajes/${empresaId}/${mensajeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const removidos = contarListasActualizadas(response.data?.listasActualizadas);
      message.success(
        removidos > 0
          ? `Teléfono eliminado correctamente. También se quitó de ${removidos} lista(s) de difusión.`
          : "Teléfono eliminado correctamente."
      );
      await fetchMensajes();
    } catch (error) {
      console.error("Error al eliminar teléfono:", error);
      message.error("No se pudo eliminar el teléfono.");
    } finally {
      setDeletingKey(null);
    }
  };

  const renderCuentas = (record) => {
    const columns = [
      {
        title: "Número de Cuenta",
        dataIndex: "cuenta",
        key: "cuenta",
      },
      {
        title: "Nombre de Cuenta",
        dataIndex: "nombre",
        key: "nombre",
      },
      {
        title: "Acciones",
        key: "acciones",
        width: 160,
        render: (_, cuenta) => (
          <Popconfirm
            title="Eliminar cuenta"
            description="Se eliminará esta cuenta vinculada al teléfono."
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => eliminarCuenta(record.id, cuenta.id)}
          >
            <Button
              danger
              type="link"
              icon={<DeleteOutlined />}
              loading={deletingKey === `${record.id}-${cuenta.id}`}
            >
              Eliminar
            </Button>
          </Popconfirm>
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={record.cuentasVisibles}
        pagination={false}
        rowKey="key"
        size="small"
      />
    );
  };

  const columns = [
    {
      title: "Número de Teléfono",
      dataIndex: "numero",
      key: "numero",
    },
    {
      title: "Cuentas vinculadas",
      key: "cuentas",
      render: (_, record) => (
        <Text>
          {record.cuentasVisibles.length}
          {record.cuentasVisibles.length !== record.cuentas.length
            ? ` de ${record.cuentas.length}`
            : ""}
        </Text>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 320,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => abrirModalCuenta(record)}
            loading={savingKey === `cuenta-${record.id}`}
          >
            Agregar cuenta
          </Button>
          <Popconfirm
            title="Eliminar teléfono"
            description="Se eliminará el teléfono y todas sus cuentas vinculadas."
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => eliminarTelefono(record.id)}
          >
          <Button
            danger
            type="link"
            icon={<DeleteOutlined />}
            loading={deletingKey === record.id}
          >
            Eliminar teléfono
          </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Cuentas">
      <Space style={{ marginBottom: 16, width: "100%" }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirModalTelefono}>
          Agregar teléfono
        </Button>
        <Input
          allowClear
          placeholder="Buscar por número de teléfono"
          prefix={<SearchOutlined />}
          value={searchNumero}
          onChange={(event) => setSearchNumero(event.target.value)}
          style={{ width: 260 }}
        />
        <Input
          allowClear
          placeholder="Buscar por cuenta"
          prefix={<SearchOutlined />}
          value={searchCuenta}
          onChange={(event) => setSearchCuenta(event.target.value)}
          style={{ width: 260 }}
        />
        <Input
          allowClear
          placeholder="Buscar por nombre"
          prefix={<SearchOutlined />}
          value={searchNombre}
          onChange={(event) => setSearchNombre(event.target.value)}
          style={{ width: 260 }}
        />
        <Button onClick={limpiarFiltros}>Limpiar filtros</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={mensajesFiltrados}
        expandable={{
          expandedRowRender: renderCuentas,
          rowExpandable: (record) => record.cuentasVisibles.length > 0,
        }}
        loading={loading}
        pagination={{ pageSize: 10 }}
        rowKey="key"
      />

      <Modal
        title={`Agregar cuenta${mensajeSeleccionado?.numero ? ` a ${mensajeSeleccionado.numero}` : ""}`}
        open={modalCuentaOpen}
        okText="Agregar"
        cancelText="Cancelar"
        confirmLoading={Boolean(savingKey?.startsWith("cuenta-"))}
        onCancel={cerrarModalCuenta}
        onOk={agregarCuenta}
      >
        <Form form={formCuenta} layout="vertical">
          <Form.Item
            label="Número de Cuenta"
            name="cuenta"
            rules={[
              { required: true, message: "Ingresá el número de cuenta" },
              {
                pattern: /^\d+$/,
                message: "La cuenta debe contener solo números",
              },
            ]}
          >
            <Input placeholder="Ej: 10250" />
          </Form.Item>
          <Form.Item
            label="Nombre de Cuenta"
            name="nombre"
            rules={[{ required: true, message: "Ingresá el nombre de cuenta" }]}
          >
            <Input placeholder="Ej: CASANOVA Y MEYER" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Agregar teléfono"
        open={modalTelefonoOpen}
        okText="Agregar"
        cancelText="Cancelar"
        width={720}
        confirmLoading={savingKey === "telefono"}
        onCancel={cerrarModalTelefono}
        onOk={agregarTelefono}
      >
        <Form
          form={formTelefono}
          layout="vertical"
          initialValues={{ numero: PREFIJO_TELEFONO, cuentas: [{ cuenta: "", nombre: "" }] }}
        >
          <Form.Item
            label="Número de Teléfono"
            name="numero"
            rules={[
              { required: true, message: "Ingresá el número de teléfono" },
              {
                pattern: /^\d+$/,
                message: "El teléfono debe contener solo números",
              },
            ]}
          >
            <Input placeholder="Ej: 5493534226477" />
          </Form.Item>

          <Form.List name="cuentas">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Space
                    key={field.key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <Form.Item
                      {...field}
                      label={index === 0 ? "Número de Cuenta" : ""}
                      name={[field.name, "cuenta"]}
                      rules={[
                        { required: true, message: "Ingresá la cuenta" },
                        {
                          pattern: /^\d+$/,
                          message: "Solo números",
                        },
                      ]}
                    >
                      <Input placeholder="Cuenta" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label={index === 0 ? "Nombre de Cuenta" : ""}
                      name={[field.name, "nombre"]}
                      rules={[{ required: true, message: "Ingresá el nombre" }]}
                    >
                      <Input placeholder="Nombre" style={{ width: 280 }} />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        danger
                        type="link"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    )}
                  </Space>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>
                  Agregar otra cuenta
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Card>
  );
};

export default CCuentas;
